import {
  TE_STATE_CANCELLED,
  TE_STATE_FINALIZED,
  TE_STATE_PAUSED,
  TE_STATE_STARTED,
} from "./coachExerciseNavigation";
import { normalizeKotMode } from "./kotCoachSubmit";

/** @typedef {{ total: number, submitted: number, pending: number, allSubmitted: boolean }} KotSubmissionSummary */

function snapshotEmittedMs(snap) {
  const t = snap?.emitted_at;
  if (!t) return 0;
  const ms = Date.parse(t);
  return Number.isNaN(ms) ? 0 : ms;
}

function mergeLeaderboardRows(prev = [], incoming = []) {
  const byPlayer = new Map();
  for (const row of prev) {
    if (row?.player_id) byPlayer.set(String(row.player_id), { ...row });
  }
  for (const row of incoming) {
    if (!row?.player_id) continue;
    const key = String(row.player_id);
    const old = byPlayer.get(key);
    if (!old) {
      byPlayer.set(key, { ...row });
      continue;
    }
    const oldPts = Number(old.total_points) || 0;
    const newPts = Number(row.total_points) || 0;
    const oldPlayed = Number(old.matches_played) || 0;
    const newPlayed = Number(row.matches_played) || 0;
    if (
      newPts > oldPts ||
      (newPts === oldPts && newPlayed >= oldPlayed)
    ) {
      byPlayer.set(key, { ...old, ...row });
    }
  }
  return Array.from(byPlayer.values()).sort(
    (a, b) =>
      (Number(b.total_points) || 0) - (Number(a.total_points) || 0) ||
      (Number(b.matches_won) || 0) - (Number(a.matches_won) || 0)
  );
}

function isTerminalMatchState(state) {
  return state === "WON" || state === "ABANDONED";
}

/** Prefer terminal scores over stale IN_PROGRESS / SCHEDULED rows. */
function mergeOneMatchRow(prev, incoming) {
  if (!prev) return incoming ? { ...incoming } : null;
  if (!incoming) return { ...prev };
  const prevTerm = isTerminalMatchState(prev.match_state);
  const incTerm = isTerminalMatchState(incoming.match_state);
  if (prevTerm && !incTerm) {
    return { ...incoming, ...prev };
  }
  return { ...prev, ...incoming };
}

function mergeMatchRows(prev = [], incoming = [], currentRound = null) {
  const incRoundNum = Number(currentRound?.round_number) || 0;
  const incRoundId = currentRound?.kot_round_id
    ? String(currentRound.kot_round_id)
    : null;

  const byId = new Map();
  for (const m of Array.isArray(prev) ? prev : []) {
    if (m?.kot_match_id) byId.set(String(m.kot_match_id), { ...m });
  }
  for (const m of Array.isArray(incoming) ? incoming : []) {
    if (!m?.kot_match_id) continue;
    const key = String(m.kot_match_id);
    byId.set(key, mergeOneMatchRow(byId.get(key), m));
  }

  let merged = Array.from(byId.values());
  if (incRoundId) {
    merged = merged.filter(
      (m) =>
        !m?.kot_round_id ||
        String(m.kot_round_id) === incRoundId ||
        isTerminalMatchState(m?.match_state)
    );
  } else if (incRoundNum > 0) {
    merged = merged.filter(
      (m) =>
        !m?.round_number ||
        Number(m.round_number) === incRoundNum ||
        isTerminalMatchState(m?.match_state)
    );
  }
  return merged;
}

/** Merge socket snapshots without losing newer leaderboard / match rows. */
export function mergeKotSnapshots(prev, incoming) {
  if (!incoming) return prev || null;
  if (!prev) return incoming;

  const prevRev = Number(prev.kot_snapshot_revision) || 0;
  const incRev = Number(incoming.kot_snapshot_revision) || 0;
  if (incRev > 0 && prevRev > 0 && incRev < prevRev) {
    return prev;
  }

  const prevMs = snapshotEmittedMs(prev);
  const incMs = snapshotEmittedMs(incoming);
  const preferIncoming = incRev > prevRev || (incRev === prevRev && incMs >= prevMs);
  const base = preferIncoming ? { ...incoming } : { ...prev };
  const other = preferIncoming ? prev : incoming;

  const currentRound =
    base.current_round || other.current_round || incoming.current_round || prev.current_round;

  base.leaderboard = mergeLeaderboardRows(prev.leaderboard, incoming.leaderboard);
  base.matches = mergeMatchRows(prev.matches, incoming.matches, currentRound);

  if (incoming.current_round && prev.current_round) {
    const incNum = Number(incoming.current_round.round_number) || 0;
    const prevNum = Number(prev.current_round.round_number) || 0;
    if (incNum >= prevNum) {
      base.current_round = { ...prev.current_round, ...incoming.current_round };
    } else {
      base.current_round = { ...incoming.current_round, ...prev.current_round };
    }
  } else {
    base.current_round = currentRound;
  }

  const prevRoundNum = Number(prev.current_round?.round_number) || 0;
  const incRoundNumCoach = Number(incoming.current_round?.round_number) || 0;
  const incomingCoachUiIsStale =
    incRoundNumCoach < prevRoundNum ||
    (prev.current_round?.round_state === "PENDING" &&
      incoming.coach_ui?.submission_summary?.pending > 0 &&
      prev.coach_ui?.can_start_round === true);
  if (incomingCoachUiIsStale && prev.coach_ui) {
    base.coach_ui = prev.coach_ui;
  } else if (incMs >= prevMs && incoming.coach_ui) {
    base.coach_ui = incoming.coach_ui;
  } else if (prev.coach_ui) {
    base.coach_ui = prev.coach_ui;
  }

  base.emitted_at =
    incMs >= prevMs
      ? incoming.emitted_at || prev.emitted_at
      : prev.emitted_at || incoming.emitted_at;

  const mergedRoundState = base.current_round?.round_state ?? null;
  const roundTimerActive =
    mergedRoundState === "IN_PROGRESS" ||
    mergedRoundState === "STOP_REQUESTED";
  if (!roundTimerActive) {
    base.round_timer = incoming.round_timer ?? null;
  } else if (incoming.round_timer) {
    base.round_timer = incoming.round_timer;
  }

  if (
    mergedRoundState === "PENDING" &&
    Array.isArray(incoming.tables) &&
    incoming.tables.length > 0
  ) {
    base.tables = incoming.tables;
  } else if (Array.isArray(incoming.tables) && incoming.tables.length > 0) {
    base.tables = incoming.tables;
  } else if (!base.tables && Array.isArray(other.tables)) {
    base.tables = other.tables;
  }

  return base;
}

/**
 * Tables a multi-table camera phone may score during King of the Table.
 * A single-table phone keeps the player's own match screen.
 * @returns {number[]} ascending, empty unless at least two tables are covered.
 */
export function cameraScoreTableNumbers(tableNumbers) {
  const list = [
    ...new Set(
      (Array.isArray(tableNumbers) ? tableNumbers : [])
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n) && n > 0)
        .map((n) => Math.floor(n))
    ),
  ].sort((a, b) => a - b);
  return list.length >= 2 ? list : [];
}

export function kotMatchesForCurrentRound(snapshot) {
  return matchesForCurrentRound(snapshot);
}

function isRealKotMatchRow(match) {
  return (
    match?.kot_match_id && !String(match.kot_match_id).startsWith("setup-")
  );
}

/**
 * Pick a match row for coach kot:request-stop-round (prefer first decided table).
 */
export function pickMatchForKotStopRequest(snapshot) {
  const matches = kotMatchesForCurrentRound(snapshot).filter(isRealKotMatchRow);
  if (!matches.length) return null;
  const won = matches.find((m) => m.match_state === "WON");
  if (won) return won;
  const inProgress = matches.find((m) => m.match_state === "IN_PROGRESS");
  if (inProgress) return inProgress;
  return matches[0];
}

/** Coach may stop an active STOP_SCORE_LEAD round (before STOP_REQUESTED). */
export function canCoachRequestStopRound(snapshot, config) {
  const mode = normalizeKotMode(
    config?.match_winner_mode || config?.kot_mode
  );
  if (mode !== "STOP_SCORE_LEAD") return false;
  if (snapshot?.current_round?.round_state !== "IN_PROGRESS") return false;
  const match = pickMatchForKotStopRequest(snapshot);
  if (!match?.table_id || !match.player_a_id) return false;
  return true;
}

/** Player may stop when they have an IN_PROGRESS match in STOP_SCORE_LEAD mode. */
export function canPlayerRequestStopRound(snapshot, config, viewerPlayerId) {
  if (!viewerPlayerId) return false;
  const mode = normalizeKotMode(
    config?.match_winner_mode || config?.kot_mode
  );
  if (mode !== "STOP_SCORE_LEAD") return false;
  if (snapshot?.current_round?.round_state !== "IN_PROGRESS") return false;
  const pid = String(viewerPlayerId);
  const matches = kotMatchesForCurrentRound(snapshot).filter(isRealKotMatchRow);
  const myMatch = matches.find(
    (m) =>
      m.match_state === "IN_PROGRESS" &&
      ((m.player_a_id != null && String(m.player_a_id) === pid) ||
        (m.player_b_id != null && String(m.player_b_id) === pid))
  );
  return Boolean(myMatch?.table_id && myMatch?.kot_match_id);
}

/** Resolve the match row a player should use for kot:request-stop-round. */
export function pickPlayerMatchForKotStopRequest(snapshot, viewerPlayerId) {
  if (!viewerPlayerId || !snapshot) return null;
  const pid = String(viewerPlayerId);
  const matches = kotMatchesForCurrentRound(snapshot).filter(isRealKotMatchRow);
  return (
    matches.find(
      (m) =>
        m.match_state === "IN_PROGRESS" &&
        ((m.player_a_id != null && String(m.player_a_id) === pid) ||
          (m.player_b_id != null && String(m.player_b_id) === pid))
    ) || null
  );
}

function matchesForCurrentRound(snapshot) {
  if (!snapshot || !Array.isArray(snapshot.matches)) return [];
  const round = snapshot.current_round;
  const allMatches = snapshot.matches;
  if (!round) return allMatches;

  if (round.round_state === "PENDING") {
    const awaitingCoach =
      snapshot?.coach_ui?.awaiting_coach_start === true ||
      snapshot?.coach_ui?.can_start_round === true;
    if (awaitingCoach) {
      const roundId = round.kot_round_id ? String(round.kot_round_id) : null;
      if (roundId) {
        const nextRoundMatches = allMatches.filter(
          (m) =>
            m?.kot_round_id &&
            String(m.kot_round_id) === roundId &&
            !isTerminalMatchState(m?.match_state)
        );
        if (nextRoundMatches.length > 0) return nextRoundMatches;
      }
      // Fall back to terminal rows only while the next-round snapshot is still
      // catching up (no SCHEDULED matches yet).
      const terminal = allMatches.filter((m) =>
        isTerminalMatchState(m?.match_state)
      );
      if (terminal.length > 0) return terminal;
      const prevRoundNum = (Number(round.round_number) || 1) - 1;
      if (prevRoundNum > 0) {
        const byRoundNum = allMatches.filter(
          (m) => Number(m?.round_number) === prevRoundNum
        );
        if (byRoundNum.length > 0) return byRoundNum;
      }
    }
  }

  const roundId = round.kot_round_id ? String(round.kot_round_id) : null;
  if (roundId) {
    const withRoundId = allMatches.filter(
      (m) => m?.kot_round_id && String(m.kot_round_id) === roundId
    );
    const withoutRoundId = allMatches.filter((m) => !m?.kot_round_id);
    if (withRoundId.length > 0) {
      const tableIds = new Set(
        withRoundId.map((m) => (m.table_id ? String(m.table_id) : null)).filter(Boolean)
      );
      const extras = withoutRoundId.filter(
        (m) => m?.table_id && !tableIds.has(String(m.table_id))
      );
      return [...withRoundId, ...extras];
    }
    return allMatches;
  }

  const roundNum = Number(round.round_number);
  if (Number.isFinite(roundNum) && roundNum > 0) {
    const filtered = allMatches.filter(
      (m) => !m?.round_number || Number(m.round_number) === roundNum
    );
    return filtered.length > 0 ? filtered : allMatches;
  }
  return allMatches;
}

/** Table cards for coach UI: matches plus seated tables missing a match row. */
export function kotTableDisplayRows(snapshot) {
  const matches = matchesForCurrentRound(snapshot);
  const round = snapshot?.current_round;
  const awaitingCoach =
    round?.round_state === "PENDING" &&
    (snapshot?.coach_ui?.awaiting_coach_start === true ||
      snapshot?.coach_ui?.can_start_round === true);
  const hasUpcomingMatches = matches.some(
    (m) => m.match_state === "SCHEDULED" || m.match_state === "IN_PROGRESS"
  );
  if (
    awaitingCoach &&
    !hasUpcomingMatches &&
    matches.some((m) => isTerminalMatchState(m?.match_state))
  ) {
    return matches.sort(
      (a, b) => (Number(a.table_number) || 0) - (Number(b.table_number) || 0)
    );
  }

  const tables = Array.isArray(snapshot?.tables) ? snapshot.tables : [];
  if (tables.length === 0) return matches;

  const byTableId = new Map();
  for (const m of matches) {
    if (m?.table_id) byTableId.set(String(m.table_id), m);
  }

  const rows = [...matches];
  for (const table of tables) {
    const tid = table?.table_id ? String(table.table_id) : null;
    if (!tid || byTableId.has(tid)) continue;
    const players = Array.isArray(table.players) ? table.players : [];
    const p1 = players.find((p) => p.position === "Player1") || players[0];
    const p2 = players.find((p) => p.position === "Player2");
    if (!p1?.player_id) continue;
    if (!p2?.player_id) {
      rows.push({
        kot_match_id: `waiting-${tid}`,
        table_id: tid,
        table_number: table.table_number,
        player_a_id: p1.player_id,
        player_b_id: null,
        player_a: p1,
        player_b: null,
        match_state: "WAITING",
        is_lone_waiting: true,
        player_a_score: null,
        player_b_score: null,
      });
      continue;
    }
    if (!p1 || !p2) continue;
    rows.push({
      kot_match_id: `pending-${tid}`,
      table_id: tid,
      table_number: table.table_number,
      player_a_id: p1.player_id,
      player_b_id: p2.player_id,
      player_a: p1,
      player_b: p2,
      match_state: "SCHEDULED",
      player_a_score: null,
      player_b_score: null,
    });
  }

  return rows.sort(
    (a, b) => (Number(a.table_number) || 0) - (Number(b.table_number) || 0)
  );
}

function applyLocalLeaderboardFromMatch(snap, match, payload) {
  if (!snap || !match) return snap;
  const matchId = match.kot_match_id || payload?.kot_match_id;
  if (matchId && Array.isArray(snap.matches)) {
    const existing = snap.matches.find(
      (m) => String(m.kot_match_id) === String(matchId)
    );
    if (
      existing &&
      (existing.match_state === "WON" || existing.match_state === "ABANDONED")
    ) {
      return snap;
    }
  }
  const winnerId = match.winner_player_id || payload?.winner_player_id;
  if (!winnerId) return snap;

  const playerAId = match.player_a_id ? String(match.player_a_id) : null;
  const playerBId = match.player_b_id ? String(match.player_b_id) : null;
  const loserId =
    playerAId && String(winnerId) === playerAId
      ? playerBId
      : playerAId;

  const winPts =
    Number(match.points_awarded_winner ?? payload?.points_awarded_winner) || 0;
  const losePts =
    Number(match.points_awarded_loser ?? payload?.points_awarded_loser) || 0;
  const winnerGamePts = playerGamePointsFromMatch(
    { ...match, ...payload },
    winnerId
  );
  const loserGamePts = playerGamePointsFromMatch(
    { ...match, ...payload },
    loserId
  );

  const rows = Array.isArray(snap.leaderboard) ? [...snap.leaderboard] : [];
  const bump = (playerId, { won, points, gamePoints }) => {
    if (!playerId) return;
    const key = String(playerId);
    const idx = rows.findIndex((r) => String(r.player_id) === key);
    if (idx >= 0) {
      const row = rows[idx];
      rows[idx] = {
        ...row,
        matches_played: (Number(row.matches_played) || 0) + 1,
        matches_won: (Number(row.matches_won) || 0) + (won ? 1 : 0),
        matches_lost: (Number(row.matches_lost) || 0) + (won ? 0 : 1),
        total_points: (Number(row.total_points) || 0) + points,
        game_points_scored:
          (Number(row.game_points_scored) || 0) + (Number(gamePoints) || 0),
      };
      return;
    }
    rows.push({
      player_id: key,
      matches_played: 1,
      matches_won: won ? 1 : 0,
      matches_lost: won ? 0 : 1,
      total_points: points,
      game_points_scored: Number(gamePoints) || 0,
    });
  };

  bump(String(winnerId), {
    won: true,
    points: winPts,
    gamePoints: winnerGamePts,
  });
  bump(loserId, { won: false, points: losePts, gamePoints: loserGamePts });

  return {
    ...snap,
    leaderboard: rows.sort(
      (a, b) => (Number(b.total_points) || 0) - (Number(a.total_points) || 0)
    ),
  };
}

/**
 * @param {Array<{ match_state?: string }>|null|undefined} matches
 * @returns {KotSubmissionSummary}
 */
export function computeSubmissionSummary(matches) {
  const list = Array.isArray(matches) ? matches : [];
  const total = list.length;
  const submitted = list.filter(
    (m) => m.match_state === "WON" || m.match_state === "ABANDONED"
  ).length;
  const pending = list.filter(
    (m) => m.match_state === "IN_PROGRESS" || m.match_state === "SCHEDULED"
  ).length;
  return {
    total,
    submitted,
    pending,
    allSubmitted: total > 0 && pending === 0,
  };
}

/**
 * @param {object} params
 * @param {string|null|undefined} params.roundState
 * @param {string|null|undefined} params.exerciseStateId
 * @param {KotSubmissionSummary} params.submissionSummary
 * @param {object|null|undefined} params.coachUi
 * @param {boolean} [params.isExerciseOver]
 */
export function computeCanStartRound({
  roundState,
  exerciseStateId,
  coachUi,
  isExerciseOver = false,
}) {
  if (isExerciseOver) return false;
  if (
    exerciseStateId === TE_STATE_PAUSED ||
    exerciseStateId === TE_STATE_FINALIZED ||
    exerciseStateId === TE_STATE_CANCELLED
  ) {
    return false;
  }
  // Prefer server-side eligibility (exercise STARTED + round PENDING).
  if (coachUi && typeof coachUi.can_start_round === "boolean") {
    return coachUi.can_start_round;
  }
  if (exerciseStateId && exerciseStateId !== TE_STATE_STARTED) {
    return false;
  }
  return roundState === "PENDING";
}

/**
 * @param {object|null|undefined} snapshot
 * @param {string|null|undefined} exerciseStateId
 */
export function deriveKotCoachRoundState(snapshot, exerciseStateId) {
  const roundState = snapshot?.current_round?.round_state ?? null;
  const roundNumber = snapshot?.current_round?.round_number ?? null;
  const coachUi = snapshot?.coach_ui ?? null;
  const roundMatches = matchesForCurrentRound(snapshot);
  const submissionSummary = coachUi?.submission_summary
    ? {
        total: Number(coachUi.submission_summary.total) || 0,
        submitted: Number(coachUi.submission_summary.submitted) || 0,
        pending: Number(coachUi.submission_summary.pending) || 0,
        allSubmitted: !!coachUi.submission_summary.all_submitted,
      }
    : computeSubmissionSummary(roundMatches);
  const isExerciseOver =
    snapshot?.current_round == null &&
    snapshot?.config?.max_rounds != null &&
    Number(snapshot?.config?.current_round_number) >=
      Number(snapshot?.config?.max_rounds);
  const canStartRound = computeCanStartRound({
    roundState,
    exerciseStateId,
    coachUi,
    isExerciseOver:
      isExerciseOver || exerciseStateId === TE_STATE_FINALIZED,
  });
  const isActiveRound =
    roundState === "IN_PROGRESS" || roundState === "STOP_REQUESTED";
  const roundClosing = coachUi?.round_closing === true;
  const allMatchesDone =
    submissionSummary.allSubmitted && submissionSummary.total > 0;
  const allTablesSubmitted =
    coachUi?.all_tables_submitted === true ||
    (allMatchesDone && isActiveRound);
  const awaitingCoachStart =
    coachUi?.awaiting_coach_start === true || roundState === "PENDING";
  const preparingNextRound =
    (roundClosing || (awaitingCoachStart && !canStartRound && allTablesSubmitted));
  const showSubmissionProgress =
    (isActiveRound || roundClosing) && !canStartRound;
  const tableDataSyncPending =
    submissionSummary.total > 0 &&
    roundMatches.length < submissionSummary.total;

  return {
    roundState,
    roundNumber,
    submissionSummary,
    canStartRound,
    awaitingCoachStart,
    preparingNextRound,
    allTablesSubmitted,
    showSubmissionProgress,
    roundClosing,
    tableDataSyncPending,
    coachUi,
  };
}

/** KoT classement dialog — rank players by match wins. */
export const KOT_LEADERBOARD_RANK_MODES = {
  VICTORIES: "victories",
  POINTS: "points",
};

function playerGamePointsFromMatch(match, playerId) {
  if (!match || playerId == null) return 0;
  const pid = String(playerId);
  const aId =
    match.player_a_id != null ? String(match.player_a_id) : null;
  const bId =
    match.player_b_id != null ? String(match.player_b_id) : null;
  const aScore = Number(match.player_a_score);
  const bScore = Number(match.player_b_score);
  if (aId === pid && Number.isFinite(aScore)) return aScore;
  if (bId === pid && Number.isFinite(bScore)) return bScore;
  return 0;
}

/** Sum of a player's own match scores from finished matches. */
export function aggregateGamePointsFromMatches(playerId, matches = []) {
  if (!playerId || !Array.isArray(matches)) return 0;
  let total = 0;
  for (const match of matches) {
    if (match?.match_state !== "WON" && match?.match_state !== "ABANDONED") {
      continue;
    }
    total += playerGamePointsFromMatch(match, playerId);
  }
  return total;
}

/** Match points scored by one player (not KoT ranking points). */
export function kotLeaderboardGamePointsScored(row, matches = []) {
  const fromRow = Number(row?.game_points_scored);
  if (row?.game_points_scored != null && Number.isFinite(fromRow)) {
    return Math.max(0, fromRow);
  }
  return aggregateGamePointsFromMatches(row?.player_id, matches);
}

/** KoT session ranking points for one leaderboard row. */
export function kotLeaderboardRankingPoints(row) {
  return Math.max(0, Number(row?.total_points) || 0);
}

/** KoT session victories for one leaderboard row. */
export function kotLeaderboardVictories(row) {
  return Math.max(0, Number(row?.matches_won) || 0);
}

/**
 * Sort KoT leaderboard rows for the player/coach classement dialog.
 * @param {Array} rows
 * @param {"victories"|"points"} mode
 * @param {Array} [matches] finished-match fallback for game_points_scored
 */
export function sortKotLeaderboardRows(
  rows = [],
  mode = KOT_LEADERBOARD_RANK_MODES.VICTORIES,
  matches = []
) {
  const list = Array.isArray(rows) ? [...rows] : [];
  list.sort((a, b) => {
    if (mode === KOT_LEADERBOARD_RANK_MODES.POINTS) {
      return (
        kotLeaderboardGamePointsScored(b, matches) -
          kotLeaderboardGamePointsScored(a, matches) ||
        kotLeaderboardVictories(b) - kotLeaderboardVictories(a)
      );
    }
    return (
      kotLeaderboardVictories(b) - kotLeaderboardVictories(a) ||
      kotLeaderboardGamePointsScored(b, matches) -
        kotLeaderboardGamePointsScored(a, matches)
    );
  });
  return list;
}

/** Player/coach classement — full victories label. */
export function formatKotLeaderboardVictories(row, t) {
  const count = kotLeaderboardVictories(row);
  return t("kotVictoriesCount", {
    count,
    defaultValue: `${count} win`,
    defaultValue_plural: `${count} wins`,
  });
}

/** Compact victories label for tight leaderboard rows. */
export function formatKotLeaderboardVictoriesShort(row, t) {
  const count = kotLeaderboardVictories(row);
  return (
    t("kotVictoriesShort", { count, defaultValue: `${count}W` }) || `${count}W`
  );
}

/** Each post-submit interstitial phase (rotation preview, then classement). */
export const KOT_POST_SUBMIT_PHASE_MS = 10000;

/** Lone player waiting on the highest table (odd player count). */
export function findLonePlayerOnLastTableFromSnapshot(snapshot) {
  const tables = sortedKotActiveTables(snapshot);
  if (tables.length < 2) return null;
  const last = tables[tables.length - 1];
  const seating = (snapshot?.tables || []).find(
    (t) =>
      (t?.table_id && String(t.table_id) === String(last.table_id)) ||
      Number(t?.table_number) === last.table_number
  );
  if (!seating) return null;
  const players = Array.isArray(seating.players) ? seating.players : [];
  if (players.length !== 1) return null;
  const p = players[0];
  if (!p?.player_id) return null;
  return {
    player_id: String(p.player_id),
    table_id: last.table_id,
    table_number: last.table_number,
    position: p.position === "Player2" ? "Player2" : "Player1",
  };
}

/** Viewer is alone on the last table with no match this round. */
export function isViewerKotLoneWaiting(snapshot, viewerPlayerId, roundState) {
  if (!snapshot || viewerPlayerId == null) return false;
  if (roundState !== "IN_PROGRESS" && roundState !== "STOP_REQUESTED") {
    return false;
  }
  const lone = findLonePlayerOnLastTableFromSnapshot(snapshot);
  if (!lone) return false;
  if (String(lone.player_id) !== String(viewerPlayerId)) return false;
  const hasOpenMatch = (snapshot.matches || []).some(
    (m) =>
      kotMatchInvolvesPlayer(m, viewerPlayerId) &&
      !isTerminalMatchState(m?.match_state)
  );
  return !hasOpenMatch;
}

function assignClientRotationPositions(drafts) {
  const byDestination = new Map();
  drafts.forEach((d) => {
    const key = String(d.to_table_number);
    if (!byDestination.has(key)) byDestination.set(key, []);
    byDestination.get(key).push(d);
  });
  const entries = [];
  byDestination.forEach((group) => {
    group
      .sort((a, b) => a.from_table_number - b.from_table_number)
      .forEach((draft, positionIndex) => {
        entries.push({
          ...draft,
          position: positionIndex === 0 ? "Player1" : "Player2",
        });
      });
  });
  return entries;
}

/**
 * Client-side full rotation preview (all WON matches + lone bye).
 * Mirrors backend `computeRotation` in kingOfTable.pure.js.
 */
export function previewFullRoundRotation(snapshot) {
  const tables = sortedKotActiveTables(snapshot);
  if (!tables.length) return [];

  const lone = findLonePlayerOnLastTableFromSnapshot(snapshot);
  const wonMatches = matchesForCurrentRound(snapshot).filter(
    (m) => m?.match_state === "WON" && m.winner_player_id
  );

  const drafts = [];
  for (const m of wonMatches) {
    const fromTable = Number(m.table_number);
    if (!Number.isFinite(fromTable)) continue;
    const idx = tables.findIndex((t) => t.table_number === fromTable);
    if (idx < 0) continue;
    const winnerId = String(m.winner_player_id);
    const loserId =
      String(m.player_a_id) === winnerId
        ? String(m.player_b_id)
        : String(m.player_a_id);
    const winnerTarget = tables[Math.max(0, idx - 1)];
    const loserTarget = tables[Math.min(tables.length - 1, idx + 1)];
    drafts.push({
      player_id: winnerId,
      from_table_number: fromTable,
      to_table_number: winnerTarget.table_number,
      to_table_id: winnerTarget.table_id,
      outcome: "won",
    });
    drafts.push({
      player_id: loserId,
      from_table_number: fromTable,
      to_table_number: loserTarget.table_number,
      to_table_id: loserTarget.table_id,
      outcome: "lost",
    });
  }

  if (lone?.player_id) {
    const loneIdx = tables.findIndex((t) => t.table_number === lone.table_number);
    if (loneIdx > 0) {
      const winnerTarget = tables[loneIdx - 1];
      drafts.push({
        player_id: String(lone.player_id),
        from_table_number: lone.table_number,
        to_table_number: winnerTarget.table_number,
        to_table_id: winnerTarget.table_id,
        outcome: "won",
      });
    }
  }

  return assignClientRotationPositions(drafts);
}

/** Sorted active tables from snapshot seating (ascending table number). */
export function sortedKotActiveTables(snapshot) {
  const fromSeating = (snapshot?.tables || [])
    .filter((t) => t?.table_number != null)
    .map((t) => ({
      table_id: t.table_id ?? null,
      table_number: Number(t.table_number),
    }))
    .sort((a, b) => a.table_number - b.table_number);
  if (fromSeating.length > 0) return fromSeating;
  const numbers = [
    ...new Set(
      (snapshot?.matches || [])
        .map((m) => Number(m?.table_number))
        .filter((n) => Number.isFinite(n))
    ),
  ].sort((a, b) => a - b);
  return numbers.map((table_number) => ({ table_id: null, table_number }));
}

/**
 * Client-side preview of where players on one table move after the round closes.
 * Mirrors iOS `KingOfTableRotationPreview`.
 */
export function previewRotationForMatch(match, snapshot) {
  if (!match || match.match_state !== "WON" || !match.winner_player_id) {
    return [];
  }
  const fromTable = Number(match.table_number);
  if (!Number.isFinite(fromTable)) return [];
  const tables = sortedKotActiveTables(snapshot);
  if (!tables.length) return [];
  const idx = tables.findIndex((t) => t.table_number === fromTable);
  if (idx < 0) return [];

  const winnerId = String(match.winner_player_id);
  const loserId =
    String(match.player_a_id) === winnerId
      ? String(match.player_b_id)
      : String(match.player_a_id);
  const winnerTarget = tables[Math.max(0, idx - 1)];
  const loserTarget = tables[Math.min(tables.length - 1, idx + 1)];

  const drafts = [
    {
      player_id: winnerId,
      from_table_number: fromTable,
      to_table_number: winnerTarget.table_number,
      to_table_id: winnerTarget.table_id,
      outcome: "WON",
    },
    {
      player_id: loserId,
      from_table_number: fromTable,
      to_table_number: loserTarget.table_number,
      to_table_id: loserTarget.table_id,
      outcome: "LOST",
    },
  ];

  const byDestination = new Map();
  drafts.forEach((d) => {
    const key = String(d.to_table_number);
    if (!byDestination.has(key)) byDestination.set(key, []);
    byDestination.get(key).push(d);
  });
  const entries = [];
  byDestination.forEach((group) => {
    group
      .sort((a, b) => a.from_table_number - b.from_table_number)
      .forEach((draft, positionIndex) => {
        entries.push({
          ...draft,
          position: positionIndex === 0 ? "Player1" : "Player2",
        });
      });
  });
  return entries;
}

/** Player has submitted and is waiting for other tables before the round closes. */
export function isKotWaitingAfterSubmit(snapshot, match, exerciseStateId) {
  if (!match || match.match_state !== "WON") return false;
  const roundState = snapshot?.current_round?.round_state ?? null;
  if (roundState !== "IN_PROGRESS" && roundState !== "STOP_REQUESTED") {
    return false;
  }
  const coach = deriveKotCoachRoundState(snapshot, exerciseStateId);
  return !coach.awaitingCoachStart;
}

/** Find rotation move for one player after a completed round. */
export function findViewerKotRotationMove(rotation, viewerPlayerId) {
  if (!viewerPlayerId || !Array.isArray(rotation)) return null;
  return (
    rotation.find((m) => String(m.player_id) === String(viewerPlayerId)) || null
  );
}

/**
 * Player-facing message: move up/down/stay after round rotation.
 * @returns {{ direction: 'up'|'down'|'stay'|null, tableNumber: number|null, message: string|null }}
 */
export function resolveViewerKotTableMove(rotation, viewerPlayerId, t) {
  const move = findViewerKotRotationMove(rotation, viewerPlayerId);
  if (!move) return { direction: null, tableNumber: null, message: null };

  const from = Number(move.from_table_number ?? move.from_table);
  const to = Number(move.to_table_number ?? move.to_table);
  if (!Number.isFinite(to)) {
    return { direction: null, tableNumber: null, message: null };
  }

  let direction = "stay";
  if (Number.isFinite(from) && to < from) direction = "up";
  else if (Number.isFinite(from) && to > from) direction = "down";

  let message;
  if (direction === "up") {
    message =
      t("kotPlayerMoveUpTable", { number: to }) ||
      `Move up to table ${to}`;
  } else if (direction === "down") {
    message =
      t("kotPlayerMoveDownTable", { number: to }) ||
      `Move down to table ${to}`;
  } else if (to === 1) {
    message =
      t("kotPlayerWonTopTable") ||
      "Congratulations! You stay at the top table — wait for your challenger.";
  } else {
    message =
      t("kotPlayerMoveStayTable", { number: to }) ||
      t("table_with_number", { number: to });
  }

  return { direction, tableNumber: to, message };
}

/** Destination table number for the viewer after rotation preview. */
export function resolveViewerKotDestinationTable(rotation, viewerPlayerId) {
  const move = findViewerKotRotationMove(rotation, viewerPlayerId);
  if (!move) return null;
  const to = Number(move.to_table_number ?? move.to_table);
  return Number.isFinite(to) ? to : null;
}

function kotMatchInvolvesPlayer(match, playerId) {
  if (!match || playerId == null) return false;
  const pid = String(playerId);
  return (
    (match.player_a_id != null && String(match.player_a_id) === pid) ||
    (match.player_b_id != null && String(match.player_b_id) === pid)
  );
}

function buildKotMatchFromRotation(rotation, viewerPlayerId, roundId) {
  if (!Array.isArray(rotation) || viewerPlayerId == null) return null;
  const move = rotation.find(
    (m) => String(m.player_id) === String(viewerPlayerId)
  );
  if (!move) return null;
  const destTable = Number(move.to_table_number ?? move.to_table);
  if (!Number.isFinite(destTable)) return null;
  const atDest = rotation.filter(
    (m) => Number(m.to_table_number ?? m.to_table) === destTable
  );
  if (atDest.length < 2) return null;
  const sorted = [...atDest].sort((a, b) => {
    const ap = a.position === "Player2" ? 1 : 0;
    const bp = b.position === "Player2" ? 1 : 0;
    return ap - bp;
  });
  const p1 = sorted[0];
  const p2 = sorted[1];
  return {
    kot_match_id: `preview-${destTable}`,
    kot_round_id: roundId,
    table_id: move.to_table_id ?? p1.to_table_id ?? null,
    table_number: destTable,
    player_a_id: p1.player_id,
    player_b_id: p2.player_id,
    match_state: "SCHEDULED",
    player_a_score: null,
    player_b_score: null,
  };
}

function buildKotMatchFromTable(table, roundId) {
  const players = Array.isArray(table?.players) ? table.players : [];
  if (players.length < 2) return null;
  const p1 =
    players.find((p) => p.position === "Player1") || players[0];
  const p2 =
    players.find((p) => p.position === "Player2") ||
    players.find((p) => String(p.player_id) !== String(p1.player_id)) ||
    players[1];
  if (!p1?.player_id || !p2?.player_id) return null;
  const tableId = table.table_id ?? `table-${table.table_number}`;
  return {
    kot_match_id: `pending-${tableId}`,
    kot_round_id: roundId,
    table_id: table.table_id ?? null,
    table_number: table.table_number,
    player_a_id: p1.player_id,
    player_b_id: p2.player_id,
    player_a: p1,
    player_b: p2,
    match_state: "SCHEDULED",
    player_a_score: null,
    player_b_score: null,
  };
}

/**
 * Match row for player UI after rotation — prefers the upcoming SCHEDULED
 * pairing at the viewer's destination table instead of the just-finished WON row.
 *
 * @param {object|null} snapshot
 * @param {string|number|null} viewerPlayerId
 * @param {Array|null} [rotationOverride] — client preview while round is still active
 */
export function resolveKotViewerMatchForDisplay(
  snapshot,
  viewerPlayerId,
  rotationOverride = null
) {
  if (!snapshot || viewerPlayerId == null) return null;

  const round = snapshot.current_round;
  const rotation =
    Array.isArray(rotationOverride) && rotationOverride.length > 0
      ? rotationOverride
      : snapshot.rotation;
  const awaitingCoach =
    round?.round_state === "PENDING" &&
    (snapshot?.coach_ui?.awaiting_coach_start === true ||
      snapshot?.coach_ui?.can_start_round === true);
  const preferUpcoming = awaitingCoach || (Array.isArray(rotation) && rotation.length > 0);

  if (preferUpcoming) {
    const roundId = round?.kot_round_id ? String(round.kot_round_id) : null;
    const allMatches = Array.isArray(snapshot.matches) ? snapshot.matches : [];
    if (roundId) {
      const scheduled = allMatches.find(
        (m) =>
          String(m?.kot_round_id) === roundId &&
          (m.match_state === "SCHEDULED" || m.match_state === "IN_PROGRESS") &&
          kotMatchInvolvesPlayer(m, viewerPlayerId)
      );
      if (scheduled) return scheduled;
    }

    const destTable = resolveViewerKotDestinationTable(rotation, viewerPlayerId);
    const tables = Array.isArray(snapshot.tables) ? snapshot.tables : [];
    const table =
      destTable != null
        ? tables.find((t) => Number(t.table_number) === destTable)
        : tables.find((t) =>
            (t.players || []).some(
              (p) => String(p.player_id) === String(viewerPlayerId)
            )
          );
    const fromTable = buildKotMatchFromTable(table, roundId);
    if (fromTable) return fromTable;

    const fromRotation = buildKotMatchFromRotation(
      rotation,
      viewerPlayerId,
      roundId
    );
    if (fromRotation) return fromRotation;
  }

  const current = kotMatchesForCurrentRound(snapshot).find((m) =>
    kotMatchInvolvesPlayer(m, viewerPlayerId)
  );
  return current || null;
}

/**
 * Drop never-played next-round rows when the exercise ends so clients do not
 * show a phantom in-progress match after the final round.
 */
function stripPhantomMatchesAfterExerciseEnd(next, payload) {
  if (payload?.is_exercise_over !== true || !Array.isArray(next.matches)) {
    return next;
  }
  const completedRoundId = payload.kot_round_id
    ? String(payload.kot_round_id)
    : payload.prev_round?.kot_round_id
      ? String(payload.prev_round.kot_round_id)
      : null;
  if (!completedRoundId) {
    next.matches = next.matches.filter((m) => isTerminalMatchState(m?.match_state));
    return next;
  }
  next.matches = next.matches.filter(
    (m) =>
      String(m?.kot_round_id) === completedRoundId &&
      isTerminalMatchState(m?.match_state)
  );
  return next;
}

/**
 * Merge kot:round-completed fields into a snapshot shell.
 */
export function applyKotRoundCompletedPatch(prev, teId, tsId, payload) {
  const base = prev || {
    te_id: String(teId),
    ts_id: String(tsId),
    matches: [],
    leaderboard: [],
    tables: [],
    config: null,
    current_round: null,
    round_timer: null,
  };
  const next = { ...base, te_id: String(teId), ts_id: String(tsId) };
  const isRotationFinalize = payload?.rotation_finalize === true;

  if (Array.isArray(payload.leaderboard)) {
    next.leaderboard = payload.leaderboard;
  }

  if (isRotationFinalize) {
    if (payload.current_round) {
      next.current_round = payload.current_round;
    } else if (payload.next_round) {
      next.current_round = payload.next_round;
    } else if (payload.is_exercise_over) {
      next.current_round = null;
    }
    if (payload.rotation) {
      next.rotation = payload.rotation;
    }
    if (Array.isArray(payload.tables) && payload.tables.length > 0) {
      next.tables = payload.tables;
    }
    if (payload.coach_ui) {
      next.coach_ui = payload.coach_ui;
    }
    next.round_timer = null;
    if (payload.is_exercise_over) {
      next.current_round = null;
      next.coach_ui = {
        ...(next.coach_ui || {}),
        awaiting_coach_start: false,
        can_start_round: false,
        round_closing: false,
      };
    }
    return stripPhantomMatchesAfterExerciseEnd(next, payload);
  }

  if (payload.current_round) {
    next.current_round = payload.current_round;
  } else if (payload.next_round) {
    next.current_round = payload.next_round;
  } else if (payload.is_exercise_over) {
    next.current_round = null;
  }
  if (payload.rotation) {
    next.rotation = payload.rotation;
  }
  if (Array.isArray(payload.tables) && payload.tables.length > 0) {
    next.tables = payload.tables;
  }
  if (payload.coach_ui) {
    next.coach_ui = payload.coach_ui;
  }
  if (payload.kot_round_id && Array.isArray(next.matches)) {
    const completedRoundId = String(payload.kot_round_id);
    const completed = next.matches.filter(
      (m) => String(m?.kot_round_id) === completedRoundId
    );
    if (completed.length > 0) {
      next.matches = completed;
    }
  }
  next.round_timer = null;
  if (payload.is_exercise_over) {
    next.current_round = null;
    next.coach_ui = {
      ...(next.coach_ui || {}),
      awaiting_coach_start: false,
      can_start_round: false,
      round_closing: false,
    };
  }
  return stripPhantomMatchesAfterExerciseEnd(next, payload);
}

function mergeMatchRow(matches, patch) {
  if (!patch || !patch.kot_match_id) {
    return Array.isArray(matches) ? matches : [];
  }
  const list = Array.isArray(matches) ? [...matches] : [];
  const idx = list.findIndex(
    (m) => String(m.kot_match_id) === String(patch.kot_match_id)
  );
  const merged = {
    ...(idx >= 0 ? list[idx] : {}),
    ...patch,
    kot_match_id: patch.kot_match_id,
  };
  if (idx >= 0) {
    list[idx] = merged;
  } else {
    list.push(merged);
  }
  return list;
}

/** Merge partial kot socket event fields into a snapshot. */
export function applyKotEventPatch(prev, teId, tsId, patch) {
  const base = prev || {
    te_id: String(teId),
    ts_id: String(tsId),
    matches: [],
    leaderboard: [],
    tables: [],
    config: null,
    current_round: null,
    round_timer: null,
  };
  const next = { ...base, te_id: String(teId), ts_id: String(tsId) };
  if (Array.isArray(patch.leaderboard)) {
    next.leaderboard = patch.leaderboard;
  }
  if (patch.match) {
    next.matches = mergeMatchRow(next.matches, patch.match);
  }
  if (patch.current_round) {
    next.current_round = patch.current_round;
  }
  if (patch.next_round) {
    next.current_round = patch.next_round;
  }
  if (patch.rotation) {
    next.rotation = patch.rotation;
  }
  if (patch.coach_ui) {
    next.coach_ui = patch.coach_ui;
  }
  if (patch.round_state_after && next.current_round) {
    const currentState = next.current_round.round_state;
    // Ignore stale submit events that arrive after rotation created a PENDING round.
    if (
      currentState === "PENDING" &&
      patch.round_state_after !== "PENDING"
    ) {
      /* keep PENDING */
    } else {
      next.current_round = {
        ...next.current_round,
        round_state: patch.round_state_after,
      };
    }
  }
  return next;
}

/** Fast path after kot:match-submitted before full snapshot arrives. */
export function applyKotMatchSubmittedPatch(prev, teId, tsId, payload) {
  if (!payload) return prev;
  const matchPatch = {
    ...(payload.match || {
      kot_match_id: payload.kot_match_id,
      table_id: payload.table_id,
      winner_player_id: payload.winner_player_id,
      player_a_score: payload.player_a_score,
      player_b_score: payload.player_b_score,
      decided_by: payload.decided_by,
      match_state: "WON",
      player_a_id: payload.player_a_id,
      player_b_id: payload.player_b_id,
      points_awarded_winner: payload.points_awarded_winner,
      points_awarded_loser: payload.points_awarded_loser,
    }),
    kot_round_id:
      payload.kot_round_id ||
      payload.match?.kot_round_id ||
      prev?.current_round?.kot_round_id ||
      null,
  };
  const matchId = matchPatch.kot_match_id || payload.kot_match_id;
  const alreadyScored =
    matchId &&
    Array.isArray(prev?.matches) &&
    prev.matches.some(
      (m) =>
        String(m.kot_match_id) === String(matchId) &&
        (m.match_state === "WON" || m.match_state === "ABANDONED")
    );
  const mergedLeaderboard =
    Array.isArray(payload.leaderboard) && payload.leaderboard.length > 0
      ? mergeLeaderboardRows(prev?.leaderboard, payload.leaderboard)
      : undefined;
  const staleAfterRotation =
    !!payload.all_submitted &&
    prev?.current_round?.round_state === "PENDING" &&
    (prev?.coach_ui?.awaiting_coach_start === true ||
      prev?.coach_ui?.can_start_round === true);
  let next = applyKotEventPatch(prev, teId, tsId, {
    leaderboard: mergedLeaderboard,
    match: matchPatch,
    round_state_after: staleAfterRotation
      ? undefined
      : payload.round_state_after,
    coach_ui: staleAfterRotation ? undefined : payload.coach_ui,
  });
  if (next && !alreadyScored && !payload.idempotent) {
    next = applyLocalLeaderboardFromMatch(next, matchPatch, payload);
  }
  const roundStateAfter = next?.current_round?.round_state ?? null;
  const betweenRounds =
    payload.awaiting_coach_start === true ||
    roundStateAfter === "PENDING" ||
    next?.coach_ui?.awaiting_coach_start === true ||
    (next?.coach_ui?.round_closing === true && payload.all_submitted);
  if (betweenRounds) {
    next.round_timer = null;
  }
  return next;
}
